

















































import { State } from 'vuex-class';
import { Component, Ref, Watch } from 'vue-property-decorator';
import { Form as MtdForm } from '@ss/mtd-vue';

import {
    getRgAvailableItemList,
    getThirdRobot,
    getThirdRobotValidationDirectory,
    IGetRgAvailableItemListResponse,
    IGetThirdRobotResponse,
    updateThirdRobot
} from '@/api/rg';
import InfoTip from '@/views/components/info-tip.vue';
import AbstractRobot from './AbstractRobot';
import { getTtOrigin } from '@/utils/business';
import { createTicket } from '@/api/ticket';

type ItemList = IGetRgAvailableItemListResponse['items'];

@Component({ components: { InfoTip } })
export default class ThirdRobot extends AbstractRobot {
    readonly formMeta = {
        active: false,
        robotId: undefined
    };

    readonly formData = {
        mainPage: '',
        queryChatHistoryUrl: '',
        chatPreCheckUrl: '',
        clientId: '',
        secretKey: '',
        itemIdList: [],
        isDialogueForced: false
    };

    readonly formRules: Record<Exclude<keyof typeof ThirdRobot.prototype.formData, 'isDialogueForced'>, { required: boolean; message: string }[]> = {
        mainPage: [{ required: true, message: '不可为空' }],
        queryChatHistoryUrl: [{ required: true, message: '不可为空' }],
        chatPreCheckUrl: [{ required: true, message: '不可为空' }],
        clientId: [{ required: true, message: '不可为空' }],
        secretKey: [{ required: true, message: '不可为空' }],
        itemIdList: [{ required: true, message: '不可为空' }]
    };

    @Ref('formRef')
    readonly formRef: MtdForm | undefined;

    isFormDataValid = false;

    rgItemList: IGetRgAvailableItemListResponse['items'] = [];

    @State(state => state.cti.userInfo)
    private readonly _userInfo: CommonTypes.UserInfoItem;

    private _rgId: number | undefined;

    async created() {
        this._rgId = this._parseRgId();
        await this._initRgAvailableItems();
        await this._initRobotSetting();
    }

    async getActiveState(): Promise<boolean> {
        await this._pullRobotSetting();
        return !!this.formMeta.active;
    }

    async onShow() {
        await this._updateRobotSetting({ isActive: true });
    }

    async onHide() {
        await this._updateRobotSetting({ isActive: false });
    }

    async submit() {
        /**
         * 创建用于验证的TT工单.
         * https://km.sankuai.com/collabpage/2545610001#b-6a8971e864f64e2eb05ed89cdc58da58
         */
        const createVerifyTT = async (): Promise<number | undefined> => {
            let categoryId: number | undefined;
            let typeId: number | undefined;
            let itemId: number | undefined;
            try {
                const { data, code } = await getThirdRobotValidationDirectory();
                if (code !== 200) return undefined;
                categoryId = data.categoryId;
                typeId = data.typeId;
                itemId = data.itemId;
            } catch (_) {
                return undefined;
            }

            try {
                const randomId = Math.random().toString(36).slice(2);
                const { data, code } = await createTicket({
                    categoryId,
                    typeId,
                    itemId,
                    name: `机器人配置${randomId}, rgId: ${this._rgId}`,
                    description: `<p>rgId: ${this._rgId}</p>`,
                    ticketType: '事件',
                    assigned: 'guowenfei',
                    reporter: this._userInfo.username,
                    sla: 'S4'
                });
                return code === 200 ? data.id : undefined;
            } catch (_) {
                return undefined;
            }
        };

        const showVerifyTTToast = (ticketId: number): void => {
            const origin = getTtOrigin();
            this.$mtd.message({
                message: `保存成功，已发起TT工单，<a href="${origin}/ticket/detail?id=${ticketId}" target="_blank">点击查看</a>`,
                dangerouslyUseHTMLString: true,
                type: 'success'
            });
        };

        try {
            const { ok, isCreateVerifyTicket, message } = await this._updateRobotSetting();
            if (!ok) {
                this.$mtd.message({ message, type: 'error' });
                return;
            }

            if (isCreateVerifyTicket) {
                const ticketId = await createVerifyTT();
                if (ticketId !== undefined) showVerifyTTToast(ticketId);
            } else {
                this.$mtd.message({ message: '保存成功', type: 'success' });
            }
        } catch (_) {}
    }

    handleIsDialogueForcedChange(b: boolean): void {
        const openDialogueConfirm = (): void => {
            this.$mtd
                .confirm({
                    title: '提示',
                    message: '此开关关闭后将强制用户进入智能问答页面，为避免产生客诉，请您提前做好运营宣导。',
                    width: '400px',
                    type: 'warning',
                    okButtonText: '知道了'
                })
                .catch(() => {});
        };

        const isClosing = !b;
        if (isClosing) openDialogueConfirm();
    }

    /**
     * 由于form的validate方法在调用后会展示错误信息，所以这里不用form的validate方法，而是手动校验。
     */
    @Watch('formData', { deep: true })
    private _onFormDataChange() {
        const validate = (): boolean => {
            if (!this.formData.mainPage) return false;
            if (!this.formData.queryChatHistoryUrl) return false;
            if (!this.formData.chatPreCheckUrl) return false;
            if (!this.formData.clientId) return false;
            if (!this.formData.secretKey) return false;
            if (!this.formData.itemIdList.length) return false;
            return true;
        };

        this.isFormDataValid = validate();
    }

    private async _initRgAvailableItems() {
        try {
            const { data, code } = await getRgAvailableItemList({ rgId: this._rgId });
            if (code === 200) {
                this.rgItemList = data.items;
            }
        } catch (_) {}
    }

    private async _initRobotSetting() {
        await this._pullRobotSetting();
    }

    private async _pullRobotSetting() {
        try {
            const { data, code } = await getThirdRobot({ rgId: this._rgId });
            if (code === 200) {
                this.formMeta.active = data.isActive;
                this.formMeta.robotId = data.robotId;

                this.formData.mainPage = data.mainPage;
                this.formData.queryChatHistoryUrl = data.queryChatHistoryUrl;
                this.formData.chatPreCheckUrl = data.chatPreCheckUrl;
                this.formData.clientId = data.clientId;
                this.formData.secretKey = data.secretKey;
                this.formData.itemIdList = this._toItemIdList(data.itemList);
                this.formData.isDialogueForced = data.isDialogueForced;
            }
        } catch (_) {}
    }

    private async _updateRobotSetting(
        payload: Partial<Omit<IGetThirdRobotResponse, 'type'>> = {}
    ): Promise<{ ok: boolean; isCreateVerifyTicket: boolean; message: string }> {
        try {
            const { data, code, message } = await updateThirdRobot({
                isActive: true,
                type: 'CUSTOMER_SERVICE',
                rgId: this._rgId,
                robotId: this.formMeta.robotId,
                mainPage: this.formData.mainPage,
                queryChatHistoryUrl: this.formData.queryChatHistoryUrl,
                chatPreCheckUrl: this.formData.chatPreCheckUrl,
                clientId: this.formData.clientId,
                secretKey: this.formData.secretKey,
                itemList: this._toItemList(this.formData.itemIdList),
                isDialogueForced: this.formData.isDialogueForced,
                ...payload
            });

            if (code === 200) {
                this.formMeta.robotId = data.robotId;
                return { ok: true, isCreateVerifyTicket: data.isCreateVerifyTicket, message: '' };
            } else {
                if (code === 11048) {
                    this.$mtd.message({ message, type: 'error' });
                }
                return {
                    ok: false,
                    message: message,
                    isCreateVerifyTicket: false
                };
            }
        } catch (ex) {
            return {
                ok: false,
                message: `${ex}`,
                isCreateVerifyTicket: false
            };
        }
    }

    private _parseRgId(): number {
        return parseInt(this.$route.query.rgId as string, 10);
    }

    /**
     * 后端返回是一个itemList，前端需要的是一个itemIdList.
     */
    private _toItemIdList(itemList: ItemList): number[] {
        return itemList.map(v => v.itemId);
    }

    /**
     * 提交到到后端时，需要将itemIdList转换为itemList.
     */
    private _toItemList(itemIdList: number[]): ItemList {
        const res = [];
        for (let i = 0; i < itemIdList.length; i++) {
            for (let j = 0; j < this.rgItemList.length; j++) {
                if (itemIdList[i] === this.rgItemList[j].itemId) {
                    res.push(this.rgItemList[j]);
                }
            }
        }
        return res;
    }
}
